import { useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  useReview,
  useRevisions,
  useFindings,
  useHumanDecision,
  useDecisionBrief,
  useReviewStack,
  useReviewers,
} from '../hooks/useReviews'
import { useResolveReview } from '../hooks/useResolveReview'
import AccordionTimeline from '../components/Timeline/AccordionTimeline'
import ReviewerPanel from '../components/ReviewerPanel/ReviewerPanel'
import { parseDiff } from '../lib/parseDiff'
import type { Finding } from '../gen/types'

function normalizeStatus(status: string): string {
  if (status === 'pending_human') return 'pending'
  return status
}

interface FileStats {
  path: string
  adds: number
  dels: number
  blockingFindings: number
  totalFindings: number
}

function computeFileStats(diffText: string, findings: Finding[]): FileStats[] {
  if (!diffText) return []
  return parseDiff(diffText).map((f) => {
    let adds = 0
    let dels = 0
    for (const r of f.rows) {
      if (r.kind === 'add') adds++
      else if (r.kind === 'del') dels++
      else if (r.kind === 'pair') { adds++; dels++ }
    }
    const ff = findings.filter((fi) => fi.anchor?.path === f.path && fi.status !== 'resolved')
    return {
      path: f.path,
      adds,
      dels,
      blockingFindings: ff.filter((fi) => fi.severity === 'blocking').length,
      totalFindings: ff.length,
    }
  })
}

const PILL_BY_STATUS: Record<string, string> = {
  open: 'pill-open',
  pending: 'pill-pending',
  approved: 'pill-approved',
  rejected: 'pill-rejected',
  merged: 'pill-merged',
  abandoned: 'pill-abandoned',
}

// pillFromReviewers maps reviewer-aggregate state onto the top-right pill.
// Returns null when the reviewer panel doesn't apply (no reviewers yet, or
// the review is in a terminal status like merged/abandoned), letting the
// caller fall back to PILL_BY_STATUS[norm].
function pillFromReviewers(
  data:
    | {
        approved?: boolean
        blocked?: boolean
        approvedCount?: number
        totalCount?: number
        reviewers?: Array<{ state?: string }>
      }
    | undefined,
  norm: string,
): { label: string; cls: string } | null {
  if (!data) return null
  // Terminal review states win — don't paper over merged/abandoned with
  // reviewer aggregate.
  if (norm === 'merged' || norm === 'abandoned') return null
  if (data.blocked) return { label: 'Changes Requested', cls: 'pill-rejected' }
  if (data.approved) return { label: 'Approved', cls: 'pill-approved' }

  // Partial state: count how many reviewers have actually weighed in
  // (approved or commented). Pure pending means OPEN, not "0/N reviewed".
  const total = data.totalCount ?? data.reviewers?.length ?? 0
  if (total === 0) return null
  const reviewed = (data.reviewers ?? []).filter(
    (r) => r.state === 'approved' || r.state === 'commented',
  ).length
  if (reviewed === 0) return null // → fall through to OPEN
  return { label: `${reviewed}/${total} reviewed`, cls: 'pill-pending' }
}

function DirBase({ path }: { path: string }) {
  const parts = path.split('/')
  const base = parts.pop() ?? path
  const dir = parts.length > 0 ? parts.join('/') + '/' : ''
  return (
    <>
      {dir && <span className="text-fg-dim">{dir}</span>}
      <span className="text-fg">{base}</span>
    </>
  )
}

function FileBar({ adds, dels }: { adds: number; dels: number }) {
  const total = adds + dels
  const squares = (kind: 'add' | 'del' | 'none', n: number) =>
    Array.from({ length: n }, (_, i) => (
      <div
        key={`${kind}${i}`}
        className={[
          'w-[7px] h-[7px] rounded-[1px]',
          kind === 'add' ? 'bg-status-open/70' : '',
          kind === 'del' ? 'bg-sev-blocking/70' : '',
          kind === 'none' ? 'bg-border-default' : '',
        ].join(' ')}
      />
    ))
  if (total === 0) {
    return <div className="flex gap-[2px] items-center">{squares('none', 5)}</div>
  }
  const addSq = Math.min(5, Math.round((adds / total) * 5))
  const delSq = 5 - addSq
  return (
    <div className="flex gap-[2px] items-center">
      {squares('add', addSq)}
      {squares('del', delSq)}
    </div>
  )
}

export default function ReviewDetailPage() {
  const { tenant, repo, reviewName } = useParams<{ tenant: string; repo: string; reviewName: string }>()
  const navigate = useNavigate()

  // Translate the URL triplet to a uuid; every downstream hook keeps using
  // reviewId so we don't have to plumb the triplet through them.
  const resolved = useResolveReview(tenant, repo, reviewName)
  const reviewId = resolved.data?.reviewId ?? ''

  const { data: review, isLoading, isError } = useReview(reviewId)
  const { data: stack = [] } = useReviewStack(reviewId)
  const { data: revisions = [] } = useRevisions(reviewId)
  const { data: findings = [] } = useFindings(reviewId)
  const { data: reviewersData } = useReviewers(reviewId)
  const humanDecision = useHumanDecision(reviewId)
  const { data: decisionBrief } = useDecisionBrief(reviewId)

  const [expandKey, setExpandKey] = useState(0)
  const [collapseKey, setCollapseKey] = useState(0)
  const [revIdx, setRevIdx] = useState(0)

  const selectedRevision =
    revisions.length > 0 ? revisions[revisions.length - 1 - revIdx] ?? null : null

  const fileStats = selectedRevision?.diffFromParent
    ? computeFileStats(selectedRevision.diffFromParent, findings)
    : []

  const openFindings = findings.filter((f) => f.status !== 'resolved')
  const blockingCount = openFindings.filter((f) => f.severity === 'blocking').length
  const warnCount = openFindings.filter(
    (f) => f.severity === 'must-fix' || f.severity === 'warning',
  ).length
  const passCount = openFindings.filter(
    (f) => f.severity !== 'blocking' && f.severity !== 'must-fix' && f.severity !== 'warning',
  ).length

  const canDecide = review?.status === 'pending_human'
  const norm = review ? normalizeStatus(review.status) : ''
  // Pill text + class: reviewer aggregate drives the in-progress states
  // (changes-requested / partial approvals / all-approved). Terminal review
  // statuses (merged / abandoned) and the no-reviewers fallback retain the
  // review.status-driven label so we don't paper over external state.
  const reviewerPill = pillFromReviewers(reviewersData, norm)
  const pillLabel = reviewerPill?.label ?? norm
  const pillClass = reviewerPill?.cls ?? PILL_BY_STATUS[norm] ?? 'pill-open'

  const handleApprove = useCallback(() => {
    humanDecision.mutate({ action: 'approve', comment: '' })
  }, [humanDecision])

  const handleReject = useCallback(() => {
    humanDecision.mutate({ action: 'reject', comment: '' })
  }, [humanDecision])

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center text-fg-dim text-[12px]">
        Loading review…
      </div>
    )
  }

  if (isError || !review) {
    return (
      <div className="flex-1 flex items-center justify-center gap-3 text-fg-dim text-[12px]">
        <button className="btn btn-default" onClick={() => navigate('/reviews')}>
          ← Back
        </button>
        <span>{isError ? 'Failed to load review.' : 'Review not found.'}</span>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* PR band */}
      <div className="px-7 py-3 border-b border-border-subtle flex items-center gap-3 flex-wrap shrink-0">
        <button
          className="text-link text-[11px] hover:underline bg-transparent border-0 p-0"
          onClick={() => navigate('/reviews')}
        >
          ← Reviews
        </button>
        <span className="text-fg-dim text-[11px] font-mono">
          {`${review.tenantName}/${review.repoName}`} #{review.prNumber}
        </span>
        <h1 className="m-0 text-fg text-[18px] font-bold leading-snug min-w-0 truncate">
          {review.prTitle}
        </h1>
        <div className="ml-auto flex items-center gap-2">
          {canDecide && (
            <span className="attn-pill">
              <span className="glow" />
              your turn
            </span>
          )}
          <span className={`pill ${pillClass}`}>{pillLabel}</span>
          {review.prAuthorLogin && (
            <span className="text-fg-dim text-[11px] font-mono">by @{review.prAuthorLogin}</span>
          )}
        </div>
      </div>

      {/* Two-pane workspace — 60/40. `grid-rows-1` makes the row minmax(0, 1fr)
          so each pane can shrink and its `overflow-auto` actually engages; without
          it grid items default to min-height: auto and grow past the viewport. */}
      <div className="flex-1 grid grid-cols-[3fr_2fr] grid-rows-1 gap-0 min-h-0 overflow-hidden">
        {/* LEFT PANE */}
        <div className="overflow-auto px-3 py-3 flex flex-col gap-3 min-h-0">
          {/* Meta card */}
          <div className="card">
            <div className="card-bd flex flex-col gap-[7px]">
              {review.prAuthorLogin && (
                <MetaRow label="Author">
                  <span className="font-mono">@{review.prAuthorLogin}</span>
                </MetaRow>
              )}
              <MetaRow label="Score">
                {norm === 'pending' ? (
                  <span className="text-status-pending">● Needs decision</span>
                ) : norm === 'approved' ? (
                  <span className="text-status-approved">✓ Approved</span>
                ) : norm === 'rejected' ? (
                  <span className="text-status-rejected">✗ Rejected</span>
                ) : (
                  <span className="text-fg-dim">{norm}</span>
                )}
              </MetaRow>
              {(blockingCount > 0 || warnCount > 0 || passCount > 0) && (
                <MetaRow label="Analysis">
                  <span className="flex flex-wrap gap-[5px]">
                    {blockingCount > 0 && (
                      <span className="sev sev-blocking">⚑ {blockingCount} blocking</span>
                    )}
                    {warnCount > 0 && (
                      <span className="sev sev-must-fix">▲ {warnCount} warn</span>
                    )}
                    {passCount > 0 && (
                      <span className="sev sev-suggest">✓ {passCount} pass</span>
                    )}
                  </span>
                </MetaRow>
              )}
              <MetaRow label="Reviewers">
                <ReviewerPanel reviewId={reviewId} canAssign />
              </MetaRow>
            </div>
          </div>

          {/* Description */}
          {review.prBody && (
            <div className="card">
              <div className="card-hd">Description</div>
              <div className="card-bd">
                <pre className="m-0 whitespace-pre-wrap font-mono text-[12px] leading-[1.55] text-fg-muted">
                  {review.prBody}
                </pre>
              </div>
            </div>
          )}

          {/* Changed Files */}
          {fileStats.length > 0 && (
            <div className="card">
              <div className="card-hd">
                Changed Files
                {revisions.length > 1 && (
                  <div className="card-hd-right">
                    <select
                      className="select bg-canvas text-fg text-[11px] py-[3px] px-2 border border-border-default rounded-[3px] font-mono"
                      value={revIdx}
                      onChange={(e) => setRevIdx(Number(e.target.value))}
                    >
                      {[...revisions].reverse().map((rev, idx) => (
                        <option key={rev.id} value={idx}>
                          r{rev.seq} · {rev.sha.slice(0, 7)}
                          {rev.summary ? ` — ${rev.summary.slice(0, 28)}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="text-fg-dim text-[10px] uppercase tracking-[0.06em] [&>th]:py-[6px] [&>th]:px-3 [&>th]:text-left [&>th]:font-semibold [&>th]:border-b [&>th]:border-border-subtle">
                    <th>File</th>
                    <th className="w-7 text-center">⚑</th>
                    <th className="w-12 text-right text-status-open">+Add</th>
                    <th className="w-12 text-right text-sev-blocking">−Del</th>
                    <th className="w-[60px]" />
                  </tr>
                </thead>
                <tbody>
                  {fileStats.map((f) => (
                    <tr
                      key={f.path}
                      className="cursor-pointer hover:bg-raised [&>td]:py-[6px] [&>td]:px-3 [&>td]:border-b [&>td]:border-border-subtle"
                      onClick={() => review && navigate(`/${review.tenantName}/${review.repoName}/reviews/${review.name}/diff`)}
                    >
                      <td className="font-mono text-[11px] truncate max-w-0">
                        <DirBase path={f.path} />
                      </td>
                      <td className="text-center">
                        {f.blockingFindings > 0 && (
                          <span className="text-sev-blocking text-[11px]">
                            ⚑ {f.blockingFindings}
                          </span>
                        )}
                      </td>
                      <td className="text-right font-mono text-[11px] text-status-open">+{f.adds}</td>
                      <td className="text-right font-mono text-[11px] text-sev-blocking">−{f.dels}</td>
                      <td><FileBar adds={f.adds} dels={f.dels} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Stacked PRs */}
          {stack.length > 1 && (
            <div className="card">
              <div className="card-hd">
                Stacked PRs
                <span className="card-hd-right">
                  {`${review.tenantName}/${review.repoName}`} · {stack.length} in stack
                </span>
              </div>
              <div className="card-bd flex flex-col gap-[6px]">
                {stack.map((entry) => {
                  const isCurrent = entry.reviewId === reviewId
                  const entryNorm = normalizeStatus(entry.status)
                  const entryPill = PILL_BY_STATUS[entryNorm] ?? 'pill-open'
                  const dotChar = isCurrent
                    ? '▶'
                    : entryNorm === 'merged'
                      ? '✓'
                      : entryNorm === 'rejected'
                        ? '✗'
                        : '○'
                  return (
                    <div
                      key={entry.reviewId}
                      onClick={() => !isCurrent && navigate(`/${entry.tenantName}/${entry.repoName}/reviews/${entry.name}`)}
                      className={[
                        'flex items-start gap-3 px-3 py-2 rounded border',
                        isCurrent
                          ? 'border-accent-dim bg-accent-bg cursor-default'
                          : 'border-border-default cursor-pointer hover:bg-raised',
                      ].join(' ')}
                    >
                      <div
                        className={[
                          'w-5 text-center text-[11px] mt-[1px]',
                          isCurrent ? 'text-accent' : 'text-fg-dim',
                        ].join(' ')}
                      >
                        {dotChar}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[11px] text-fg-dim">#{entry.prNumber}</span>
                          <span className="text-[12px] text-fg truncate flex-1">{entry.prTitle}</span>
                          <span className={`pill ${entryPill}`}>{entryNorm}</span>
                        </div>
                        <div className="flex gap-3 text-[11px] text-fg-dim mt-[2px]">
                          {entry.prAuthorLogin && <span>@{entry.prAuthorLogin}</span>}
                          {isCurrent && <span className="text-accent">← you are here</span>}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT PANE. overflow-hidden + flex column with min-h-0 hands the
            scroll responsibility to the Timeline card below; the sticky
            header / decision brief stay fixed at the top of the pane. */}
        <div className="overflow-hidden px-3 py-3 flex flex-col gap-3 min-h-0 border-l border-border-subtle">
          {/* Sticky pane header */}
          <div className="sticky top-0 z-[5] flex items-center gap-2 px-1 py-1 bg-page/95 backdrop-blur-[2px]">
            <span className="text-fg-dim text-[10px] uppercase tracking-[0.09em] font-bold">
              Timeline
            </span>
            {openFindings.length > 0 && (
              <span className="text-fg-dim text-[10px] font-mono">
                · {openFindings.length} unresolved
              </span>
            )}
            <button
              className="ml-auto text-fg-dim hover:text-fg-muted text-[10px] bg-transparent border-0 p-0"
              onClick={() => setCollapseKey((k) => k + 1)}
            >
              collapse all
            </button>
            <button
              className="text-fg-dim hover:text-fg-muted text-[10px] bg-transparent border-0 p-0"
              onClick={() => setExpandKey((k) => k + 1)}
            >
              expand all
            </button>
          </div>

          {/* Decision Brief */}
          {canDecide && decisionBrief && (
            <div className="card border-l-[3px] border-l-accent">
              <div className="card-hd">
                Decision Brief
                <div className="card-hd-right">
                  <button
                    className="btn btn-approve"
                    onClick={handleApprove}
                    disabled={humanDecision.isPending}
                  >
                    ✓ Approve
                  </button>
                  <button
                    className="btn btn-reject"
                    onClick={handleReject}
                    disabled={humanDecision.isPending}
                  >
                    ✗ Reject
                  </button>
                </div>
              </div>
              <div className="card-bd">
                <p className="m-0 text-[13px] text-fg leading-[1.55]">
                  {decisionBrief.oneLineAsk}
                </p>
                {decisionBrief.humanRequiredReasons.length > 0 && (
                  <ul className="list-none m-0 mt-2 p-0 flex flex-col gap-[3px]">
                    {decisionBrief.humanRequiredReasons.map((r, i) => (
                      <li key={i} className="text-[12px] text-fg-muted leading-[1.55] pl-3 relative before:content-['›'] before:absolute before:left-0 before:text-accent">
                        {r}
                      </li>
                    ))}
                  </ul>
                )}
                {decisionBrief.blastRadius && (
                  <div className="mt-2 text-[11px] text-fg-dim font-mono">
                    {decisionBrief.blastRadius}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timeline. The card itself is the scroller (flex-1 min-h-0 +
              overflow-y-auto) so the timeline scrolls within the pane
              regardless of how the surrounding grid cell sizes itself. */}
          <div className="card flex-1 min-h-0 overflow-y-auto">
            <AccordionTimeline
              reviewId={reviewId}
              reviewBasePath={review ? `/${review.tenantName}/${review.repoName}/reviews/${review.name}` : undefined}
              findings={findings}
              expandKey={expandKey}
              collapseKey={collapseKey}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3 text-[12px]">
      <span className="w-[80px] shrink-0 uppercase text-[10px] tracking-[0.06em] text-fg-dim font-semibold">
        {label}
      </span>
      <span className="text-fg flex items-center gap-1">{children}</span>
    </div>
  )
}
